import { DrawerContext, DrawerConfig, DrawerItem } from './types';

export class DrawerRouter {
  constructor(
    private enableNesting: boolean = true,
    private mobileOptimization: boolean = true
  ) {}

  canNest(parentType: DrawerItem['type'], childType: DrawerItem['type']): boolean {
    if (!this.enableNesting) return false;
    
    const nestingRules = {
      'modal': ['modal', 'sheet', 'side-drawer'],
      'sheet': ['sheet', 'side-drawer'],
      'bottom-drawer': ['modal'], // Bottom drawers can only nest modals
      'side-drawer': ['modal', 'sheet', 'side-drawer'],
      'fullscreen': [] // Fullscreen drawers shouldn't nest
    };
    
    const allowedTypes = nestingRules[parentType as keyof typeof nestingRules];
    return allowedTypes ? (allowedTypes as string[]).includes(childType as string) : true;
  }

  getOptimalType(context: DrawerContext): DrawerItem['type'] {
    const { isMobile, contentComplexity, contentType, userPreference } = context;
    
    // User preference takes precedence
    if (userPreference === 'modal') return 'modal';
    if (userPreference === 'drawer') return isMobile ? 'bottom-drawer' : 'side-drawer';
    
    // Auto-routing based on context
    if (isMobile) {
      // Mobile routing
      switch (contentType) {
        case 'form':
          return contentComplexity === 'complex' ? 'fullscreen' : 'bottom-drawer';
        case 'list':
          return 'bottom-drawer';
        case 'details':
          return 'fullscreen';
        case 'actions':
          return 'bottom-drawer';
        case 'navigation':
          return 'bottom-drawer';
        default:
          return 'bottom-drawer';
      }
    } else {
      // Desktop routing
      switch (contentType) {
        case 'form':
          return contentComplexity === 'complex' ? 'modal' : 'side-drawer';
        case 'list':
          return 'side-drawer';
        case 'details':
          return 'modal';
        case 'actions':
          return 'side-drawer';
        case 'navigation':
          return 'side-drawer';
        default:
          return 'modal';
      }
    }
  }

  shouldReplace(current: DrawerItem, incoming: Omit<DrawerItem, 'level' | 'timestamp'>): boolean {
    // Replace if both are replaceable
    if (incoming.config.replaceable && current.config.replaceable) {
      return true;
    }
    
    // Replace if same type and related content
    if (incoming.type === current.type && incoming.config.replaceable) {
      return true;
    }
    
    // Replace if mobile optimization suggests it
    if (this.mobileOptimization && incoming.config.mobileOptimized && current.config.mobileOptimized) {
      return true;
    }
    
    return false;
  }

  getOptimalConfig(type: DrawerItem['type'], context: DrawerContext): DrawerConfig {
    const { isMobile, contentComplexity, contentType } = context;
    
    const baseConfig: DrawerConfig = {
      mobileOptimized: isMobile,
    };
    
    switch (type) {
      case 'modal':
        return {
          ...baseConfig,
          size: contentComplexity === 'complex' ? 'full' : 'lg',
          height: contentComplexity === 'complex' ? '90vh' : 'auto',
        };
        
      case 'sheet':
        return {
          ...baseConfig,
          side: isMobile ? 'bottom' : 'right',
          size: contentComplexity === 'complex' ? 'xl' : 'md',
        };
        
      case 'bottom-drawer':
        return {
          ...baseConfig,
          side: 'bottom',
          size: contentComplexity === 'complex' ? 'full' : 'lg',
          height: contentComplexity === 'complex' ? '90vh' : '60vh',
        };
        
      case 'side-drawer':
        return {
          ...baseConfig,
          side: 'right',
          size: contentComplexity === 'complex' ? 'xl' : 'lg',
          width: contentComplexity === 'complex' ? '600px' : '400px',
        };
        
      case 'fullscreen':
        return {
          ...baseConfig,
          size: 'full',
          height: '100vh',
          width: '100vw',
        };
        
      default:
        return baseConfig;
    }
  }
}
